import React, { useState, useEffect, Fragment } from 'react';
import { AcceptedProps } from '../../interface/accepted-props';
import { elementStatus } from '../../../utils/element-status';
import { v4 as uuid } from 'uuid';
import { Image } from 'semantic-ui-react-bpm';
import { icons } from '../../../../../icons';
import { setLabelRef } from '../../../../../utils/check-text-overflow';
import { setFocusedElement, getFocusedElement, focusSelectedElement, removeFocusedElement, setTableFocusedElement } from '../../../../../utils/focus-element';
import useDeviceDetect from '../../../../../general/device-detect';
import { IField, IFieldAccessType } from '../../../../../../component/admin-module/module/users/interface/field';
import { FieldProperties } from '../../../service/field-properties.service';
import { StringProps } from '../../../service/type/string';
import { TValidationType } from '../../../service/field-properties.handler';
import { restoreCursorPosition, setCursorPosition } from './utils/cursor-position';

const UrlType: React.FC<AcceptedProps> = (props) => {
  const { width } = useDeviceDetect();
  const mobile = width <= 1366;
  const focusedElement = getFocusedElement();

  const name = props.getFieldId();
  const [refresher, setRefresher] = useState('');
  const [validateTriggered, setValidateTriggered] = useState(false);
  const [multilineCount, setMultilineCount] = useState(1);
  let onTypeTimeout = setTimeout(() => { }, 1000);
  const elementProperties = new FieldProperties(name, props.forwardedRef, new StringProps());

  const validateInput = () => {
    const schema = {
      ...props.validationSchema,
      minLength: props.accessType === IFieldAccessType.Required ? 1 : 0,
      isRequired: props.accessType === IFieldAccessType.Required
    }
    elementProperties.validate({ ...props, validationSchema: { ...schema } }, TValidationType.onBlur);
    setRefresher(uuid());
    setValidateTriggered(true)
    if (props.triggerFilter) props.triggerFilter('');

    if (typeof onTypeTimeout === 'function') {
      clearTimeout(onTypeTimeout);
    }
    onTypeTimeout = setTimeout(() => {
      if (props.reloadOnChange && props.reloadOnChange.length > 0 && props.triggerFieldReload) {
        props.triggerFieldReload(props.reloadOnChange);
      }
    }, 1000);
  }

  const setDefaultValue = () => {
    if (elementProperties.hasValue()) {
      return;
    }
    let defaultValue: any = props.defaultValue;
    if (props.defaultValue && !props.defaultValueElement) {
      const defaultValueString: string = defaultValue ? defaultValue as string : '';
      elementProperties.setCurrent({ value: defaultValueString });

    }
  }

  const focusInput = (id: string) => {
    if (mobile) {
      setFocusedElement(id);
      if (props.tableColumnRowIndex !== undefined) {
        setTableFocusedElement({
          index: props.tableColumnRowIndex,
          focusedElement: id,
          columnDisplayed: props.tableColumnDisplayed as number[]
        });
      }
    }
  }

  useEffect(() => {
    if (validateTriggered && props.defaultValueElement && props.isAddRecord) {
      const validationSchema = {
        type: 'object',
        required: [`${name}`],
        properties: {
          [name]: { minLength: props.accessType === IFieldAccessType.Required ? 1 : 0, ...props.validationSchema }
        },
      };
      elementProperties.validate({ ...props, validationSchema }, TValidationType.onBlur);
      setRefresher(uuid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateTriggered, props.defaultValueElement, props.accessType, name,
    props.forwardedRef, props.validationSchema, props.isAddRecord
  ])

  useEffect(() => {
    if (props.accessType === IFieldAccessType.Required) {
      validateInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multilineCount])

  useEffect(() => {
    if (props.multiline) {
      const len = props.forwardedRef.current[name].length;
      setMultilineCount(len === 0 ? 1 : len);
    }
    if (props.isAddRecord) {
      setDefaultValue();
    }
    if (props.accessType === IFieldAccessType.Required) {
      validateInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentElementId = props.tableColumnRowIndex ? `url-${name}-${props.tableColumnRowIndex}` : `url-${name}`;

  return <>
    {props.accessType !== IFieldAccessType.Hidden &&
      <>
        <span id={refresher} className='field-info'>
          <label
            ref={(element) => setLabelRef(element, name)}
            className={`field-label ${elementProperties.hasError() ? 'error' : ''}`}>{props.label}
          </label>
          {
            props.hint &&
            <i id={`info-${name}`} className="icon info-icon" title={props.hint}></i>
          }
        </span>
        <div className={`field-input ui labeled icon input single ${props.configName} ${elementStatus({
          forwardedRef: props.forwardedRef,
          name: name,
          multiline: props.multiline || false,
          required: props.accessType === IFieldAccessType.Required,
          fieldValueObject: elementProperties
        })}`} >
          <div className={`${props.hasIconInside ? 'hidden' : ''} ui label`} ><span role='img' aria-label=''>🔗</span></div>
          <input type="text"
            id={currentElementId}
            key={currentElementId}
            onBlur={(event) => {
              elementProperties.setCurrent({ value: event.target.value })
              if (focusedElement && focusedElement === currentElementId) {
                removeFocusedElement();
              }
              validateInput();
              if (props.throwValueOutside) {
                props.throwValueOutside()
              }
              if (props.sendTriggerToParentContainer) props.sendTriggerToParentContainer();
              if (props.automationService && props.fieldCollection) {
                const field = props.fieldCollection.find((e: IField) => e.id === props.id);
                if (field) {
                  props.automationService.didUpdateFieldValue(field, event.target.value)
                }
              }
            }}
            readOnly={props.accessType === IFieldAccessType.Readonly}
            ref={(curRef: any) => {
              if (curRef) {
                if (props.isFocus && refresher === '') {
                  curRef.focus();
                }
                if (focusedElement && focusedElement === currentElementId) {
                  focusSelectedElement(focusedElement);
                }
                elementProperties.setCurrent({ ...elementProperties.current, fieldType: 'string' });
                curRef.value = elementProperties.current.value
                restoreCursorPosition(curRef, name, props.tableId);
                return curRef
              }
            }}
            onChange={(event) => {
              elementProperties.setCurrent({ value: event.target.value })
              if (props.hasPageChangeRef) {
                props.hasPageChangeRef.current.hasChange = true;
              }
            }}
            autoFocus={props.isFocus}
            onClick={(e) => {
              focusInput(currentElementId);
              setCursorPosition(e, name, props.tableId);
            }}
            name={name}
          />
          {props.hasIconInside &&
            <div className='flex-container with-icon'>
              <Image width='15px' src={icons.black.brokenLink} />
            </div>
          }
        </div>
        {elementProperties.hasError() && props.showErrorMessage &&
          elementProperties.getError()[0].message.indexOf('Please provide value for') === -1 &&
          <Fragment>
            <small style={{ display: 'flex' }} />
            <small className='field error'
              dangerouslySetInnerHTML={{ __html: elementProperties.getError()[0].message }}
            />
          </Fragment>
        }
      </>
    }
  </>
}

export default React.memo(UrlType, (prevProps: AcceptedProps, nextProps: AcceptedProps) => {
  return prevProps.forwardedRef.current[prevProps.id].value === nextProps.forwardedRef.current[nextProps.id].value;
});